import clsx from 'clsx';
import { Chip } from 'copilot-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UnreadMessagesNotificationProps,
  useChannelActionContext,
  useChannelStateContext,
} from 'stream-chat-react';

import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { useAppSelector } from 'src/hooks/useStore';

const UnreadNotificationChip = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  const { isInternalChat } = useInternalChatContext();
  return (
    <Chip
      className={clsx(
        'text-base px-2 py-[5px]',
        isInternalChat
          ? 'bg-primary-hover border-non-hover-border'
          : 'bg-white',
      )}
      label={label}
      onClick={onClick}
      prefixIcon="ArrowDownSolid"
      variant="outlined"
    />
  );
};

// based on the default UnreadMessagesNotification component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageList/UnreadMessagesNotification.tsx
export const CustomUnreadMessagesNotification = ({
  queryMessageLimit,
  unreadCount,
}: UnreadMessagesNotificationProps) => {
  const currentUserGetStreamId = useAppSelector(
    (state) => state.user.data?.getstreamId || '',
  );
  const { channel, channelUnreadUiState } = useChannelStateContext();
  const { jumpToMessage, jumpToFirstUnreadMessage, markRead } =
    useChannelActionContext();
  const { t } = useTranslation();

  const [unreadMentionsCount, setUnreadMentionsCount] = useState(() =>
    channel.countUnreadMentions(),
  );
  useEffect(() => {
    const count = channel.countUnreadMentions();
    setUnreadMentionsCount(count);
  }, [channel, unreadCount]);

  const getFirstUnreadMention = useCallback(async () => {
    if (!channelUnreadUiState) {
      return null;
    }

    try {
      // Query messages starting from the first unread message
      const response = await channel.query({
        messages: {
          created_at_after: channelUnreadUiState.last_read,
          limit: queryMessageLimit,
        },
      });

      // Find the first message that mentions the current user
      const firstUnreadMention = response.messages.find((message) =>
        message.mentioned_users?.some(
          (user) => user.id === currentUserGetStreamId,
        ),
      );

      return firstUnreadMention || null;
    } catch (error) {
      console.error('Error querying unread messages:', error);
      return null;
    }
  }, [
    channel,
    currentUserGetStreamId,
    queryMessageLimit,
    channelUnreadUiState,
  ]);

  return (
    <div className="absolute self-center flex flex-col gap-2 justify-center items-center bottom-1.5 z-10">
      {unreadMentionsCount > 0 && (
        <UnreadNotificationChip
          label={t(($) => $.messages.unread_mentions, {
            count: unreadMentionsCount,
          })}
          onClick={() => {
            void (async () => {
              const firstUnreadMention = await getFirstUnreadMention();
              if (firstUnreadMention) {
                await jumpToMessage(firstUnreadMention.id);
                markRead();
              } else {
                await jumpToFirstUnreadMessage(queryMessageLimit);
                markRead();
              }
            })();
          }}
        />
      )}
      <UnreadNotificationChip
        label={
          unreadCount
            ? t(($) => $.messages.unread_messages, { count: unreadCount })
            : t(($) => $.messages.unread_messages_default)
        }
        onClick={() => {
          void jumpToFirstUnreadMessage(queryMessageLimit).then(() =>
            markRead(),
          );
        }}
      />
    </div>
  );
};
